import dayjs from 'dayjs';
import { normalizeEvent } from '@/function/calendar/calendar.event.normalize';
import { isOpenReminder } from '@/function/calendar/calendar.reminder';
import { type TypeWindowKey, windowKeysFor } from '@/function/calendar/calendar.window';
import { eventApi } from '@/store/api/calendar/calendar.event.api';
import { type TypeAppDispatch, store } from '@/store/store';
import type { TypeEvent } from '@/types/calendar/types.calendar';
import { dropById, upsertById } from '@/store/api/base/api.cache';

const cachedWindows = (): TypeWindowKey[] =>
    eventApi.util.selectCachedArgsForQuery(store.getState(), 'listWindow') as TypeWindowKey[];

const cachedReminderDays = (): string[] =>
    eventApi.util.selectCachedArgsForQuery(store.getState(), 'listOpenReminders') as string[];

const cachedLinks = (): { reference: string; from: string }[] =>
    eventApi.util.selectCachedArgsForQuery(store.getState(), 'listLinkedEvents') as { reference: string; from: string }[];

const linkedTo = (event: TypeEvent, reference: string, from: string): boolean =>
    event.references.some((entry) => `${entry.kind}:${entry.id}` === reference)
    && (Boolean(event.recurrence) || Date.parse(event.endAt) > dayjs(from).startOf('day').valueOf());

export const writeEvent = (dispatch: TypeAppDispatch, incoming: TypeEvent): void => {
    const event = normalizeEvent(incoming);
    for (const day of cachedReminderDays()) {
        dispatch(eventApi.util.updateQueryData('listOpenReminders', day, (draft) => {
            if (isOpenReminder(event, dayjs(day).endOf('day').toDate())) upsertById(draft, event); else dropById(draft, event.id);
        }));
    }

    for (const arg of cachedLinks()) {
        dispatch(eventApi.util.updateQueryData('listLinkedEvents', arg, (draft) => {
            if (linkedTo(event, arg.reference, arg.from)) upsertById(draft, event); else dropById(draft, event.id);
        }));
    }

    const belongs = new Set(event.recurrence ? [] : windowKeysFor(event.startAt, event.endAt));

    for (const key of cachedWindows()) {
        dispatch(eventApi.util.updateQueryData('listWindow', key, (draft) => {
            if (belongs.has(key)) upsertById(draft, event); else dropById(draft, event.id);
        }));
    }

    dispatch(eventApi.util.updateQueryData('listSeries', undefined, (draft) => {
        if (event.recurrence) upsertById(draft, event); else dropById(draft, event.id);
    }));

    dispatch(eventApi.util.updateQueryData('listAllEvents', undefined, (draft) => upsertById(draft, event)));
};

export const dropEvent = (dispatch: TypeAppDispatch, eventId: string): void => {
    for (const day of cachedReminderDays()) {
        dispatch(eventApi.util.updateQueryData('listOpenReminders', day, (draft) => dropById(draft, eventId)));
    }

    for (const arg of cachedLinks()) {
        dispatch(eventApi.util.updateQueryData('listLinkedEvents', arg, (draft) => dropById(draft, eventId)));
    }

    for (const key of cachedWindows()) {
        dispatch(eventApi.util.updateQueryData('listWindow', key, (draft) => dropById(draft, eventId)));
    }

    dispatch(eventApi.util.updateQueryData('listSeries', undefined, (draft) => dropById(draft, eventId)));
    dispatch(eventApi.util.updateQueryData('listAllEvents', undefined, (draft) => dropById(draft, eventId)));
};
